import cartOperationManager from "./CartOperationManager";

/**
 * Service to automatically flush cart operations before critical navigation events
 * Helps prevent loss of pending operations during page transitions
 */
export class CartAutoFlushService {
  private static instance: CartAutoFlushService;
  private flushHandlers: Set<() => Promise<void> | void> = new Set();
  private isInitialized = false;

  private constructor() {}

  static getInstance(): CartAutoFlushService {
    if (!CartAutoFlushService.instance) {
      CartAutoFlushService.instance = new CartAutoFlushService();
    }
    return CartAutoFlushService.instance;
  }

  /**
   * Initialize the auto-flush service
   * Sets up listeners for navigation events that should trigger flushing
   */
  initialize(): void {
    if (this.isInitialized) return;

    // Listen for navigation events (React Router)
    this.setupNavigationListeners();

    // Listen for page visibility changes
    this.setupVisibilityChangeListeners();

    // Listen for browser navigation (back/forward buttons)
    this.setupBrowserNavigationListeners();

    this.isInitialized = true;
    console.log("🚀 CartAutoFlushService initialized");
  }

  /**
   * Register a flush handler
   * These handlers will be called before navigation to flush pending operations
   */
  registerFlushHandler(handler: () => Promise<void> | void): () => void {
    this.flushHandlers.add(handler);

    return () => {
      this.flushHandlers.delete(handler);
    };
  }

  /**
   * Execute all registered flush handlers
   */
  private async executeFlushHandlers(): Promise<void> {
    console.log(
      `🔄 Auto-flushing ${this.flushHandlers.size} cart operation handlers`
    );

    const promises = Array.from(this.flushHandlers).map((handler) => {
      try {
        return Promise.resolve(handler());
      } catch (error) {
        console.error("Error in flush handler:", error);
        return Promise.resolve();
      }
    });

    try {
      await Promise.allSettled(promises);
      console.log("✅ All cart operations auto-flushed successfully");
    } catch (error) {
      console.error("❌ Error during auto-flush:", error);
    }
  }

  /**
   * Setup listeners for React Router navigation
   */
  private setupNavigationListeners(): void {
    // Listen for History API changes (pushState/replaceState)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = (...args) => {
      this.executeFlushHandlers();
      return originalPushState.apply(window.history, args);
    };

    window.history.replaceState = (...args) => {
      this.executeFlushHandlers();
      return originalReplaceState.apply(window.history, args);
    };

    // Listen for popstate events (back/forward buttons)
    window.addEventListener("popstate", () => {
      this.executeFlushHandlers();
    });
  }

  /**
   * Setup listeners for page visibility changes
   * Flushes operations when user switches tabs or minimizes browser
   */
  private setupVisibilityChangeListeners(): void {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        // Page is being hidden - flush operations
        this.executeFlushHandlers();
      }
    });

    // Also listen for page freeze events (mobile Safari)
    window.addEventListener("pagehide", () => {
      this.executeFlushHandlers();
    });
  }

  /**
   * Setup listeners for browser navigation
   */
  private setupBrowserNavigationListeners(): void {
    // Listen for beforeunload (page refresh, close, etc.)
    window.addEventListener("beforeunload", (event) => {
      // Note: We can't use async operations in beforeunload
      // So we'll attempt synchronous flush if possible
      const pendingOps = cartOperationManager.getPendingOperations();

      if (pendingOps.length > 0) {
        // Show browser warning
        event.preventDefault();
        event.returnValue =
          "Tienes cambios pendientes en tu carrito. ¿Estás seguro de que quieres salir?";
        return event.returnValue;
      }
    });
  }

  /**
   * Check if there are any pending operations across all handlers
   */
  hasPendingOperations(): boolean {
    return cartOperationManager.getPendingOperations().length > 0;
  }

  /**
   * Get the total count of pending operations
   */
  getPendingOperationsCount(): number {
    return cartOperationManager.getPendingOperations().length;
  }

  /**
   * Manually trigger flush of all operations
   */
  async flushAll(): Promise<void> {
    await this.executeFlushHandlers();
  }

  /**
   * Cleanup method to remove all listeners
   */
  cleanup(): void {
    this.flushHandlers.clear();
    this.isInitialized = false;
  }
}

export default CartAutoFlushService;
